// What every Saju data component shares: one person's birth input, and the Saju app's
// calculation for it. The local Studio server answers /__studio/components/saju/<kind>
// with what the Saju API would return (apps/course-ui/scripts/studio_components.py).
const object = value => value !== null && typeof value === 'object' && !Array.isArray(value);
export const componentError = (code, message) => Object.assign(new Error(message), { code });

/** The scores a Saju calculation gives a period (대운·세운·월운), as the app names them. */
export const SAJU_METRICS = Object.freeze({ balance: '종합운', wealth: '재물운', love: '애정운', health: '건강운' });

/** The birth input a Saju component is calculated from; yajasi is absent on records made before it existed. */
export const SUBJECT_KEYS = Object.freeze(['birthDate', 'birthTime', 'unknownTime', 'gender', 'isLunar', 'yajasi', 'year']);

// The Saju app's element colours (saju-react/src/types/saju.ts OHAENG_COLORS).
export const OHAENG_COLORS = Object.freeze({
  목: Object.freeze({ bg: '#e8f5e9', text: '#2e7d32', accent: '#4caf50' }),
  화: Object.freeze({ bg: '#ffebee', text: '#c62828', accent: '#f44336' }),
  토: Object.freeze({ bg: '#fff3e0', text: '#e65100', accent: '#ff9800' }),
  금: Object.freeze({ bg: '#fafafa', text: '#424242', accent: '#9e9e9e' }),
  수: Object.freeze({ bg: '#e3f2fd', text: '#1565c0', accent: '#2196f3' }),
});

export function validateSubject(props) {
  if (!object(props)) throw componentError('INVALID_COMPONENT', '사주 컴포넌트 설정을 확인해 주세요.');
  if (typeof props.birthDate !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(props.birthDate)
    || Number(props.birthDate.slice(0, 4)) < 1900 || Number(props.birthDate.slice(0, 4)) > 2200) throw componentError('INVALID_COMPONENT', '1900~2200년의 올바른 생년월일을 입력해 주세요.');
  const [, month, day] = props.birthDate.split('-').map(Number);
  const validDate = props.isLunar === true ? month >= 1 && month <= 12 && day >= 1 && day <= 30
    : Number.isFinite(Date.parse(`${props.birthDate}T00:00:00Z`)) && new Date(`${props.birthDate}T00:00:00Z`).toISOString().slice(0, 10) === props.birthDate;
  if (!validDate) throw componentError('INVALID_COMPONENT', '달력에 맞는 올바른 생년월일을 입력해 주세요.');
  if (typeof props.birthTime !== 'string' || !/^(?:[01]\d|2[0-3]):[0-5]\d$/.test(props.birthTime)) throw componentError('INVALID_COMPONENT', '태어난 시각을 입력해 주세요. 모르면 시각 모름을 선택하세요.');
  if (typeof props.unknownTime !== 'boolean' || typeof props.isLunar !== 'boolean' || !['male', 'female'].includes(props.gender)) throw componentError('INVALID_COMPONENT', '성별과 양력·음력, 시각 설정을 확인해 주세요.');
  if (Object.hasOwn(props, 'yajasi') && typeof props.yajasi !== 'boolean') throw componentError('INVALID_COMPONENT', '야자시 적용 여부를 확인해 주세요.');
  if (!Number.isInteger(props.year) || props.year < 1900 || props.year > 2200) throw componentError('INVALID_COMPONENT', '기준 연도는 1900~2200년이어야 합니다.');
  return props;
}

/** Whether two settings name the same person and reference year (a record without yajasi did not apply it). */
export function sameSubject(before, after) {
  return SUBJECT_KEYS.every(key => key === 'yajasi' ? (before.yajasi === true) === (after.yajasi === true) : before[key] === after[key]);
}

const UNAVAILABLE = '이 제작실에서는 사주를 계산할 수 없습니다. 사주 앱 계산은 로컬 제작실(apps/course-ui local_preview)에서 합니다. 기존 결과는 유지됩니다.';

/** The Saju app's calculation of `kind` for the component's person; the answer is shaped as the Saju API's. */
export async function requestSaju(kind, props, { fetchImpl = globalThis.fetch.bind(globalThis) } = {}) {
  validateSubject(props);
  const controller = new AbortController(), timer = setTimeout(() => controller.abort(), 90000);
  try {
    const response = await fetchImpl(`/__studio/components/saju/${kind}`, {
      method: 'POST', credentials: 'same-origin', cache: 'no-store', redirect: 'error', signal: controller.signal,
      headers: { 'Content-Type': 'application/json', 'X-EduSense-Studio': '1' },
      body: JSON.stringify({ birth_date: props.birthDate, birth_time: props.birthTime, gender: props.gender, unknown_time: props.unknownTime,
        is_lunar: props.isLunar, yajasi: props.yajasi === true, year: props.year, lang: 'ko' }),
    });
    // Only the local Studio server calculates (studio_components.py); elsewhere the route answers with
    // something that is not its JSON — the deployed Studio's page or a plain not-found.
    const json = (response.headers.get('Content-Type') ?? '').split(';')[0].trim() === 'application/json';
    if (!response.ok) {
      let problem = null;
      if (json) { try { problem = (await response.json()).error; } catch { problem = null; } }
      if (object(problem) && typeof problem.code === 'string' && typeof problem.message === 'string') throw componentError(problem.code, problem.message);
      if (!json || response.status === 404) throw componentError('SAJU_ENGINE_UNAVAILABLE', UNAVAILABLE);
      throw componentError('COMPONENT_CALCULATION_FAILED', `사주를 계산하지 못했습니다 (${response.status}). 기존 결과는 유지됩니다.`);
    }
    if (!json) throw componentError('SAJU_ENGINE_UNAVAILABLE', UNAVAILABLE);
    return await response.json();
  } catch (reason) {
    if (reason.name === 'AbortError') throw componentError('COMPONENT_CALCULATION_TIMEOUT', '사주 계산 시간이 초과되었습니다. 기존 결과는 유지됩니다.');
    throw reason;
  } finally { clearTimeout(timer); }
}

/** A stored calculation: its source, when it was made, and the data a renderer draws. */
export function validateCalculation(snapshot, source, validateData) {
  if (!object(snapshot) || Object.keys(snapshot).some(key => !['source', 'calculatedAt', 'data'].includes(key)) || snapshot.source !== source
    || typeof snapshot.calculatedAt !== 'string' || !/^\d{4}-\d\d-\d\dT\d\d:\d\d:\d\d\.\d{3}Z$/.test(snapshot.calculatedAt)
    || !Number.isFinite(Date.parse(snapshot.calculatedAt)) || !object(snapshot.data)) throw componentError('INVALID_COMPONENT', '컴포넌트의 저장된 계산 결과가 올바르지 않습니다.');
  validateData(snapshot.data);
}
